import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { Plus, Edit, Eye, BookOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { DataTable } from "@/components/common/DataTable";
import { DataPagination } from "@/components/common/DataPagination";
import { ListToolbar } from "@/components/common/ListToolbar";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { queryKeys } from "@/lib/queryKeys";
import { useUrlState } from "@/lib/urlState";
import { exportRowsAsCsv } from "@/lib/csv";

interface InstructorTestRow {
  id: string;
  title: string;
  description: string | null;
  subject: string;
  grade: string;
  topic: string;
  total_questions: number;
  is_published: boolean;
  created_at: string;
  time_limit?: number | null;
}

export default function InstructorTests() {
  const { user } = useAuth();
  const url = useUrlState();

  const search = url.get("q") ?? "";
  const statusFilter = url.get("status") ?? "all";
  const subjectFilter = url.get("subject") ?? "all";

  const { data: subjectsData = [] } = useQuery({
    queryKey: ["instructor-tests", "subjects", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("instructor_tests")
        .select("subject")
        .eq("instructor_id", user!.id)
        .is("deleted_at", null);
      if (error) throw error;
      return Array.from(new Set((data ?? []).map((r) => r.subject))).filter(Boolean);
    },
  });

  const paginated = usePaginatedQuery<InstructorTestRow>({
    queryKey: queryKeys.instructorTests.list({
      page: 0,
      pageSize: 25,
      filters: { search, statusFilter, subjectFilter, userId: user?.id },
    }),
    table: "instructor_tests",
    select: "*",
    orderBy: { column: "created_at", ascending: false },
    syncToUrl: true,
    honorSoftDelete: true,
    enabled: !!user,
    filters: (q) => {
      let next = q.eq("instructor_id", user!.id);
      if (statusFilter === "published") next = next.eq("is_published", true);
      else if (statusFilter === "draft") next = next.eq("is_published", false);
      if (subjectFilter !== "all") next = next.eq("subject", subjectFilter);
      if (search) next = next.or(`title.ilike.%${search}%,topic.ilike.%${search}%,subject.ilike.%${search}%`);
      return next;
    },
  });

  useEffect(() => {
    paginated.setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, subjectFilter]);

  const columns: ColumnDef<InstructorTestRow>[] = useMemo(
    () => [
      {
        header: "Title",
        accessorKey: "title",
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.title}</div>
            {row.original.description && (
              <div className="text-xs text-muted-foreground line-clamp-1">
                {row.original.description}
              </div>
            )}
          </div>
        ),
      },
      {
        header: "Subject",
        accessorKey: "subject",
        size: 140,
        cell: ({ row }) => <Badge variant="outline">{row.original.subject}</Badge>,
      },
      {
        header: "Grade",
        accessorKey: "grade",
        size: 80,
        cell: ({ row }) => `Grade ${row.original.grade}`,
      },
      {
        header: "Questions",
        accessorKey: "total_questions",
        size: 100,
      },
      {
        header: "Status",
        size: 100,
        cell: ({ row }) => (
          <Badge variant={row.original.is_published ? "default" : "secondary"}>
            {row.original.is_published ? "Published" : "Draft"}
          </Badge>
        ),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        size: 120,
        cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
      },
      {
        id: "actions",
        size: 160,
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/instructor/tests/${row.original.id}/edit`}>
                <Edit className="h-4 w-4" />
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/test/invitation?test=${row.original.id}`}>
                <Eye className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  const handleExport = () => {
    const rows = paginated.rows.map((t) => ({
      id: t.id,
      title: t.title,
      subject: t.subject,
      grade: t.grade,
      topic: t.topic,
      total_questions: t.total_questions,
      status: t.is_published ? "published" : "draft",
      created_at: t.created_at,
    }));
    exportRowsAsCsv(`instructor-tests-page-${paginated.page + 1}.csv`, rows);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">My Tests</h1>
            <p className="text-muted-foreground">Manage your created tests</p>
          </div>
          <Button asChild>
            <Link to="/instructor/tests/create">
              <Plus className="h-4 w-4 mr-2" />
              Create New Test
            </Link>
          </Button>
        </div>

        <ListToolbar
          search={search}
          onSearchChange={(v) => url.set({ q: v || null })}
          searchPlaceholder="Search tests…"
          onExportCsv={handleExport}
          filters={
            <>
              <Select value={statusFilter} onValueChange={(v) => url.set({ status: v === "all" ? null : v })}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
              <Select value={subjectFilter} onValueChange={(v) => url.set({ subject: v === "all" ? null : v })}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subjects</SelectItem>
                  {subjectsData.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          }
        />

        <div className="mt-3">
          <DataTable
            columns={columns}
            data={paginated.rows}
            isLoading={paginated.isLoading}
            isError={paginated.isError}
            onRetry={paginated.refetch}
            emptyTitle={paginated.total === 0 ? "No tests created yet" : "No tests match your filters"}
            emptyDescription="Try adjusting your search or create a new test."
            getRowId={(row) => row.id}
          />
          <DataPagination
            page={paginated.page}
            pageSize={paginated.pageSize}
            total={paginated.total}
            pageCount={paginated.pageCount}
            onPageChange={paginated.setPage}
            onPageSizeChange={paginated.setPageSize}
            isFetching={paginated.isFetching}
          />
        </div>

        {paginated.total === 0 && !paginated.isLoading && (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <Button asChild>
              <Link to="/instructor/tests/create">
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Test
              </Link>
            </Button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
